/**
 * THE ONE ABOUT CONTROL, AND WHY EXPLANATIONS LIVE BEHIND IT.
 *
 * The Owner's finding about the previous workspace was not that it explained
 * too little; it was that every screen explained itself in the middle of the
 * screen. The explanation was true and it was in the way. So each page keeps
 * its explanation — what it is for, how to use it, what it cannot show — one
 * press away, and the page itself opens on the records.
 *
 * WHAT DOES NOT MOVE BEHIND THIS BUTTON. A failure, a security refusal, a
 * state that needs the Owner, and a research limitation that bears on the
 * answer stay in the open. The About panel is for orientation, never for a
 * warning: hiding a warning behind "About" would be the exact kind of calm
 * that this product must not sell.
 *
 * THE PANEL IS A LIGHTWEIGHT DIALOG, NOT A ROUTE. Opening it is not a place
 * the Owner navigated to, so it does not change the address, does not stack a
 * history entry, and closing it returns to the same scroll position. Escape
 * closes it, the button reports its state, and focus returns to the button.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

export interface AboutContent {
	/** What this page is for, in one or two sentences. */
	purpose: ComponentChildren;
	/** How to use it. Optional — a page whose use is obvious says less. */
	how?: ComponentChildren;
	/** What it cannot show, cannot decide, or does not cover. */
	limits?: ComponentChildren;
}

export function About({ title, content }: { title: string; content: AboutContent }) {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const closeRef = useRef<HTMLButtonElement | null>(null);

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		// FOCUS MOVES TO THE CLOSE CONTROL AND BACK. A dialog that opens without
		// moving focus leaves a keyboard reader at the button that opened it,
		// with the content announced nowhere.
		requestAnimationFrame(() => closeRef.current?.focus());
		return () => document.removeEventListener('keydown', onKey);
	}, [open]);

	const close = () => {
		setOpen(false);
		requestAnimationFrame(() => buttonRef.current?.focus());
	};

	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				class="about-button"
				aria-expanded={open}
				aria-haspopup="dialog"
				onClick={() => setOpen((current) => !current)}
			>
				<span aria-hidden="true">?</span> About
			</button>
			{open && (
				<div class="about-layer">
					<button type="button" class="about-scrim" aria-label="Close About" onClick={close} />
					<div class="about-card" role="dialog" aria-modal="false" aria-label={`About ${title}`}>
						<div class="about-head">
							<h2>{title}</h2>
							<button ref={closeRef} type="button" class="button ghost small" onClick={close}>
								Close
							</button>
						</div>
						<div class="about-body">
							<p>{content.purpose}</p>
							{content.how !== undefined && (
								<>
									<h3>How to use it</h3>
									<div>{content.how}</div>
								</>
							)}
							{content.limits !== undefined && (
								<>
									<h3>What it does not cover</h3>
									<div>{content.limits}</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
}

/**
 * A PAGE'S TITLE ROW. The h1 (which the specimens and a screen reader read) and
 * the About control, and nothing else — no subtitle repeated under a header
 * that already says the same words.
 */
export function PageHeader({
	id,
	title,
	about,
	children,
}: {
	id?: string;
	title: string;
	about: AboutContent;
	children?: ComponentChildren;
}) {
	return (
		<header class="view-head page-head">
			<div class="page-head-row">
				<h1 id={id}>{title}</h1>
				<About title={title} content={about} />
			</div>
			{children}
		</header>
	);
}

/**
 * TECHNICAL RECORD — the identifiers that were once the first thing a reader
 * saw. Still on the record, still one press away, never the title.
 */
export function Technical({ summary, children }: { summary?: string; children: ComponentChildren }) {
	return (
		<details class="technical">
			<summary>{summary ?? 'Technical details'}</summary>
			<div class="technical-body">{children}</div>
		</details>
	);
}
